import { useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronDown, Download, ExternalLink, Eye, FilePlus2, Printer, ReceiptText } from 'lucide-react'
import { toast } from 'sonner'
import { financeApi } from '@/services/finance'
import { jobsApi } from '@/services/jobs'
import { orgTreeApi } from '@/services/org-tree'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { withServiceInstanceId } from '@/lib/service-instance'
import { getLocationReturnPath, resolveReturnToPath, withReturnTo } from '@/lib/return-navigation'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import type { Job, JobInvoicePayment } from '@/types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const invoiceStatusClasses: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-800',
  issued: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  void: 'bg-red-100 text-red-800',
}

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toBooleanFlag = (value: unknown) => value === true
  || value === 1
  || ['1', 'true', 'yes'].includes(String(value ?? '').trim().toLowerCase())

const getParticularBillableQuantity = (item: {
  billable_quantity?: number | string | null
  quantity?: number | string | null
}) => {
  const billable = toNumber(item.billable_quantity)
  if (billable > 0) {
    return billable
  }
  return toNumber(item.quantity)
}

const getParticularClientLineTotal = (item: {
  client_line_total?: number | string | null
  line_total?: number | string | null
  unit_price?: number | string | null
  billable_quantity?: number | string | null
  quantity?: number | string | null
}) => {
  const clientTotal = toNumber(item.client_line_total)
  if (clientTotal > 0) {
    return clientTotal
  }
  const lineTotal = toNumber(item.line_total)
  if (lineTotal > 0) {
    return lineTotal
  }
  return getParticularBillableQuantity(item) * toNumber(item.unit_price)
}

const escapeHtml = (value?: string | number | null) => {
  const source = value == null ? '' : String(value)
  return source
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const compactText = (...parts: Array<string | number | null | undefined>) =>
  parts.map((part) => String(part ?? '').trim()).filter(Boolean).join(', ')

const readString = (source: unknown, keys: string[]) => {
  if (!source || typeof source !== 'object') return ''
  const record = source as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

const normalizeThemeColor = (value: string) => {
  const trimmed = value.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`
  return '#0f172a'
}

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = normalizeThemeColor(hex).slice(1)
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ]
}

const lightenHexColor = (hex: string, whiteRatio = 0.82) => {
  const [red, green, blue] = hexToRgb(hex)
  const mix = (channel: number) => Math.round(channel + (255 - channel) * whiteRatio)
  return `rgb(${mix(red)}, ${mix(green)}, ${mix(blue)})`
}

const buildCompanyProfile = (serviceInstance: unknown, fallbackName: string, parentServiceInstance?: unknown) => {
  const config = serviceInstance && typeof serviceInstance === 'object'
    ? (serviceInstance as Record<string, unknown>).config
    : null
  const parentConfig = parentServiceInstance && typeof parentServiceInstance === 'object'
    ? (parentServiceInstance as Record<string, unknown>).config
    : null
  const configRecord = config && typeof config === 'object' ? config : null
  const parentConfigRecord = parentConfig && typeof parentConfig === 'object' ? parentConfig : null
  const source = serviceInstance && typeof serviceInstance === 'object' ? serviceInstance : {}
  const parentSource = parentServiceInstance && typeof parentServiceInstance === 'object' ? parentServiceInstance : {}
  const sourceRecord = source as Record<string, unknown>
  const parentSourceRecord = parentSource as Record<string, unknown>
  const ownerProfile = sourceRecord.owner_profile && typeof sourceRecord.owner_profile === 'object'
    ? sourceRecord.owner_profile
    : null
  const branchAdminProfile = sourceRecord.branch_admin_profile && typeof sourceRecord.branch_admin_profile === 'object'
    ? sourceRecord.branch_admin_profile
    : null
  const parentOwnerProfile = parentSourceRecord.owner_profile && typeof parentSourceRecord.owner_profile === 'object'
    ? parentSourceRecord.owner_profile
    : null
  const name = readString(parentConfigRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(parentSource, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || readString(configRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(source, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || fallbackName
  const logoUrl = readString(configRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(source, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])
    || readString(parentConfigRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(parentSource, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])
  const registrationBody = readString(parentConfigRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(parentSource, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(configRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(source, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
  const registrationName = readString(parentConfigRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(parentSource, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(configRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(source, ['company_registration_name', 'registered_company_name', 'legal_name'])
  const registrationNumber = readString(parentConfigRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(parentSource, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])
    || readString(configRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(source, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])
  const registrationDetails = [
    registrationBody,
    registrationName && registrationName !== name ? registrationName : '',
    registrationNumber,
  ].filter(Boolean).join(' · ')
  const branchName = readString(source, ['name', 'title', 'branch_name', 'service_instance_name'])
  const displayBranchName = branchName && branchName !== name ? branchName : ''
  const email = readString(configRecord, ['invoice_email', 'branch_email', 'contact_email', 'email'])
    || readString(source, ['email', 'branch_email', 'admin_email', 'branch_admin_email', 'primary_role_email', 'owner_email'])
    || readString(branchAdminProfile, ['email'])
    || readString(ownerProfile, ['email'])
    || readString(parentSource, ['email', 'admin_email', 'owner_email'])
    || readString(parentOwnerProfile, ['email'])
  const phone = readString(configRecord, ['invoice_phone', 'branch_phone', 'contact_phone', 'phone', 'phn_nbr'])
    || readString(source, ['phn_nbr', 'phone', 'branch_phone', 'admin_phone', 'branch_admin_phone', 'primary_role_phone', 'owner_phone'])
    || readString(branchAdminProfile, ['phone'])
    || readString(ownerProfile, ['phone'])
    || readString(parentSource, ['phn_nbr', 'phone', 'admin_phone', 'owner_phone'])
    || readString(parentOwnerProfile, ['phone'])
  const address = readString(configRecord, ['invoice_address', 'company_address', 'address'])
    || compactText(
      readString(source, ['address_line_1', 'location']),
      readString(source, ['address_line_2']),
      readString(source, ['city']),
      readString(source, ['postcode', 'zipcode']),
      readString(source, ['country_name']),
    )
  const primaryColor = normalizeThemeColor(
    readString(configRecord, ['primary_color', 'brand_color', 'theme_color', 'invoice_primary_color'])
      || readString(source, ['primary_color', 'brand_color', 'theme_color'])
      || '#0f172a',
  )

  return { name, branchName: displayBranchName, logoUrl, registrationBody, registrationName, registrationNumber, registrationDetails, email, phone, address, primaryColor }
}

const renderCompanyLogoHtml = (logoUrl: string, name: string) => (
  logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(name)} logo" />`
    : `<div class="logo-fallback">${escapeHtml(name.slice(0, 2).toUpperCase())}</div>`
)

const getPaymentWalletLabel = (payment: JobInvoicePayment) => {
  if (payment.payment_method_label?.trim()) {
    return payment.payment_method_label.trim()
  }
  const method = payment.payment_method
  if (method && typeof method === 'object') {
    return method.label || method.wallet_name || method.wallet_variant_name || `Wallet ${method.id ?? ''}`.trim()
  }
  if (typeof method === 'string' || typeof method === 'number') {
    return `Wallet ${method}`
  }
  return payment.payment_source_label || 'Wallet'
}

const extractInvoicePaymentToken = (paymentUrl?: string | null) => {
  const rawUrl = String(paymentUrl ?? '').trim()
  if (!rawUrl) return ''

  try {
    const parsedUrl = new URL(rawUrl, window.location.origin)
    const invoicePayPrefix = '/invoice/pay/'
    if (parsedUrl.pathname.startsWith(invoicePayPrefix)) {
      return decodeURIComponent(parsedUrl.pathname.slice(invoicePayPrefix.length))
    }

    const legacyToken = parsedUrl.searchParams.get('token')
    if (legacyToken) return legacyToken
  } catch {
    // Fall through to simple parsing for malformed or relative URLs.
  }

  const invoicePayMatch = rawUrl.match(/\/invoice\/pay\/([^?#]+)/)
  if (invoicePayMatch?.[1]) {
    return decodeURIComponent(invoicePayMatch[1])
  }

  const legacyQueryMatch = rawUrl.match(/[?&]token=([^&]+)/)
  return legacyQueryMatch?.[1] ? decodeURIComponent(legacyQueryMatch[1]) : ''
}

const buildCurrentPortalInvoicePaymentUrl = (
  paymentUrl: string | null | undefined,
  serviceInstanceId?: string | null,
) => {
  const token = extractInvoicePaymentToken(paymentUrl)
  if (!token) return ''

  const params = new URLSearchParams()
  if (serviceInstanceId) {
    params.set('org_instance_id', serviceInstanceId)
  }
  const query = params.toString()
  return `${window.location.origin}/invoice/pay/${encodeURIComponent(token)}${query ? `?${query}` : ''}`
}

export type JobInvoicesPanelProps = {
  jobId: string
  serviceInstanceId?: string | null
  job?: Job | null
  variant?: 'page' | 'embedded' | 'a4-preview'
}

export function JobInvoicesPanel({
  jobId,
  serviceInstanceId,
  job: initialJob,
  variant = 'page',
}: JobInvoicesPanelProps) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const labels = useWorkspaceLabels()
  const entity = labels.entity
  const entityLower = labels.entityLower
  const currentReturnPath = getLocationReturnPath(location)
  const backPath = resolveReturnToPath(
    location.search,
    withServiceInstanceId(`${labels.basePath}/${jobId}`, serviceInstanceId),
  )
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<'all' | 'issued' | 'draft'>('all')
  const [paymentLinkLoadingId, setPaymentLinkLoadingId] = useState<string | number | null>(null)
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null)

  const { data: fetchedJob, isLoading: jobLoading } = useQuery({
    queryKey: ['job', jobId, serviceInstanceId ?? 'none'],
    queryFn: () => jobsApi.getJob(jobId, { service_instance_id: serviceInstanceId ?? undefined }),
    enabled: !!jobId && !initialJob,
  })

  const job = initialJob ?? fetchedJob

  const { data: invoice, isLoading: invoiceLoading } = useQuery({
    queryKey: ['invoice-preview', jobId, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoicePreview(jobId!, serviceInstanceId ?? undefined),
    enabled: !!jobId,
  })

  const serviceInstanceQuery = useQuery({
    queryKey: ['invoice-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })
  const parentServiceInstanceId = serviceInstanceQuery.data?.parent_instance_id
  const parentServiceInstanceQuery = useQuery({
    queryKey: ['invoice-parent-service-instance', parentServiceInstanceId, serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(parentServiceInstanceId!),
    enabled: Boolean(parentServiceInstanceId && parentServiceInstanceId !== serviceInstanceId),
  })

  const invoicesQuery = useQuery({
    queryKey: ['job-invoices', jobId, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoices({ job_id: jobId!, page_size: 100, service_instance_id: serviceInstanceId ?? undefined }),
    enabled: !!jobId,
  })

  const createInvoiceMutation = useMutation({
    mutationFn: () => financeApi.createInvoice({ job: jobId! }),
    onSuccess: () => {
      toast.success('Draft invoice created')
      queryClient.invalidateQueries({ queryKey: ['job-invoices', jobId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', jobId] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const issueInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string | number) => financeApi.issueInvoice(invoiceId),
    onSuccess: () => {
      toast.success('Invoice issued')
      queryClient.invalidateQueries({ queryKey: ['job-invoices', jobId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', jobId] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const markInvoicePaidMutation = useMutation({
    mutationFn: (invoiceId: string | number) => financeApi.markInvoicePaid(invoiceId),
    onSuccess: () => {
      toast.success('Invoice marked as paid')
      queryClient.invalidateQueries({ queryKey: ['job-invoices', jobId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', jobId] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const voidInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string | number) => financeApi.voidInvoice(invoiceId),
    onSuccess: () => {
      toast.success('Invoice voided')
      queryClient.invalidateQueries({ queryKey: ['job-invoices', jobId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', jobId] })
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const isLoading = (initialJob ? false : jobLoading)
    || invoiceLoading
    || invoicesQuery.isLoading
    || serviceInstanceQuery.isLoading
    || parentServiceInstanceQuery.isLoading

  const allInvoices = useMemo(() => {
    const results = invoicesQuery.data?.results ?? []
    const normalizedJobId = String(jobId ?? '').trim()
    if (!normalizedJobId) {
      return results
    }

    return results.filter((entry) => String(entry.job ?? '').trim() === normalizedJobId)
  }, [invoicesQuery.data?.results, jobId])

  if (isLoading) {
    return (
      <div className="space-y-6">
        {variant === 'page' ? <Skeleton className="h-8 w-48" /> : null}
        <Skeleton className={variant === 'embedded' ? 'h-[480px] w-full' : 'h-[600px] w-full'} />
      </div>
    )
  }

  const activeInvoices = allInvoices.filter((entry) => entry.status !== 'void')
  const voidInvoices = allInvoices.filter((entry) => entry.status === 'void')
  const filteredActiveInvoices = activeInvoices.filter((entry) => {
    if (invoiceStatusFilter === 'all') {
      return true
    }
    return entry.status === invoiceStatusFilter
  })
  const hasActiveInvoice = activeInvoices.length > 0
  const hasOnlyVoidInvoices = allInvoices.length > 0 && !hasActiveInvoice
  const latestInvoice = activeInvoices[0]
  const snapshotPreview = latestInvoice?.snapshot_document?.preview
  const snapshotHasBackendTotals = Boolean(snapshotPreview?.total_rows?.length || snapshotPreview?.tax_rows?.length || snapshotPreview?.included_tax_note)
  const invoiceBasis = snapshotHasBackendTotals ? snapshotPreview : invoice ?? snapshotPreview
  const customerPayableTotal = toNumber(invoiceBasis?.customer_payable)
  const particularsTotal = toNumber(invoiceBasis?.total_particulars)
  const billableExpensesTotal = toNumber(invoiceBasis?.total_expenses)
  const labourChargesTotal = toNumber(invoiceBasis?.total_pay)
  const grossTotal = toNumber(invoiceBasis?.grand_total)
  const jobCostTotal = grossTotal
  const advancePayment = toNumber(latestInvoice?.advance_payment ?? invoiceBasis?.advance_payment)
  const advancePaymentTransactions = latestInvoice?.advance_payments ?? invoiceBasis?.advance_payments ?? []
  const paymentDue = toNumber(latestInvoice?.payment_due ?? invoiceBasis?.payment_due)

  const clientAddress = invoiceBasis
    ? [invoiceBasis.door, invoiceBasis.first_line, invoiceBasis.second_line, invoiceBasis.city, invoiceBasis.postcode, invoiceBasis.country]
        .filter(Boolean)
        .join(', ')
    : null

  const isSnapshotFrozen = Boolean(latestInvoice?.snapshot_document?.preview || invoice?.snapshot_frozen)
  const isJobCompleted = String(job?.status ?? '').toLowerCase() === 'completed'
  const documentType = latestInvoice || isSnapshotFrozen || isJobCompleted ? 'Invoice' : 'Quotation'
  const documentTypeLower = documentType.toLowerCase()
  const serviceVariantName = String(
    serviceInstanceQuery.data?.service_variant_name
    ?? serviceInstanceQuery.data?.service_variant_label
    ?? serviceInstanceQuery.data?.service_variant_service
    ?? serviceInstanceQuery.data?.service_data?.service_name
    ?? ''
  ).trim()
  const invoiceBrandName = serviceVariantName || 'Ellan One'
  const companyProfile = buildCompanyProfile(serviceInstanceQuery.data, invoiceBrandName, parentServiceInstanceQuery.data)
  const invoiceLabel = latestInvoice?.invoice_number || latestInvoice?.invoice_no || (isJobCompleted ? 'Draft' : 'Quotation')
  const taxAmount = toNumber(invoiceBasis?.tax_amount)
  const includedTaxAmount = toNumber(invoiceBasis?.included_tax_amount)
  const taxBreakdown = invoiceBasis?.tax_breakdown ?? []
  const invoiceDate = latestInvoice?.issued_at || latestInvoice?.created_at || new Date().toISOString()
  const dueDate = latestInvoice?.due_date || invoiceBasis?.due_date || null
  const summaryRows: Array<[string, string]> = [
    ['Customer payable', formatCurrency(customerPayableTotal)],
    ['Inventory particulars', formatCurrency(particularsTotal)],
    ['Billable expenses', formatCurrency(billableExpensesTotal)],
    ['Labour charges', formatCurrency(labourChargesTotal)],
    ['Tax added', formatCurrency(taxAmount)],
    ['Tax included', formatCurrency(includedTaxAmount)],
    ['Invoice total', formatCurrency(jobCostTotal)],
    ['Advance payment', formatCurrency(advancePayment)],
    ['Payment due', formatCurrency(paymentDue)],
  ]

  const handleCreateDraftInvoice = () => {
    if (hasActiveInvoice) {
      toast.error(`This ${entityLower} already has an active invoice. Void it before creating a new one.`)
      return
    }

    createInvoiceMutation.mutate()
  }

  const openPaymentLink = async (entry: (typeof allInvoices)[number]) => {
    setPaymentLinkLoadingId(entry.id)
    try {
      const freshInvoice = await financeApi.getInvoice(entry.id, serviceInstanceId)
      const paymentUrl = buildCurrentPortalInvoicePaymentUrl(
        freshInvoice?.payment_url || entry.payment_url,
        serviceInstanceId,
      )

      if (!paymentUrl) {
        throw new Error('Payment link is not available for this invoice.')
      }

      window.open(paymentUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to generate payment link')
    } finally {
      setPaymentLinkLoadingId(null)
    }
  }

  const renderInvoiceCard = (entry: (typeof allInvoices)[number]) => (
    <div key={String(entry.id)} className="rounded-lg border border-border/70 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-foreground">{entry.invoice_number || entry.invoice_no}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Created {entry.created_at ? formatDate(entry.created_at) : 'just now'}
            {entry.issued_at ? ` • Issued ${formatDate(entry.issued_at)}` : ''}
          </p>
        </div>
        <Badge className={invoiceStatusClasses[entry.status] || invoiceStatusClasses.draft}>
          {entry.status.replace('_', ' ')}
        </Badge>
      </div>
      <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
        <div className="flex justify-between">
          <span>Gross total</span>
          <span className="font-medium text-foreground">{formatCurrency(toNumber(entry.snapshot_grand_total))}</span>
        </div>
        <div className="flex justify-between">
          <span>Advance payment</span>
          <span className="font-medium text-foreground">{formatCurrency(toNumber(entry.advance_payment))}</span>
        </div>
        <div className="flex justify-between">
          <span>Payment due</span>
          <span className="font-medium text-foreground">{formatCurrency(toNumber(entry.payment_due))}</span>
        </div>
      </div>
      {entry.status === 'void' ? null : (
        <div className="mt-4 flex gap-2">
          {entry.status === 'draft' ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (!isJobCompleted) {
                  toast.error(`${entity} must be completed before issuing an invoice.`)
                  return
                }
                issueInvoiceMutation.mutate(entry.id)
              }}
              disabled={issueInvoiceMutation.isPending || !isJobCompleted}
            >
              Issue
            </Button>
          ) : null}
          {entry.status === 'issued' ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => markInvoicePaidMutation.mutate(entry.id)}
              disabled={markInvoicePaidMutation.isPending}
            >
              Mark Paid
            </Button>
          ) : null}
          {entry.status === 'issued' ? (
            <Button asChild size="sm" variant="outline">
              <Link to={withReturnTo(withServiceInstanceId(`/finance/invoices/${entry.id}/payments`, serviceInstanceId), currentReturnPath)}>
                Payments
              </Link>
            </Button>
          ) : null}
          {entry.status === 'issued' ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void openPaymentLink(entry)}
              disabled={paymentLinkLoadingId === entry.id}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {paymentLinkLoadingId === entry.id ? 'Generating link...' : 'Open payment link'}
            </Button>
          ) : null}
          {entry.status === 'draft' || entry.status === 'issued' ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => voidInvoiceMutation.mutate(entry.id)}
              disabled={voidInvoiceMutation.isPending}
            >
              Void
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )

  const buildInvoiceA4Html = () => {
    const tableHeaderColor = lightenHexColor(companyProfile.primaryColor)
    const tableHeaderBorderColor = lightenHexColor(companyProfile.primaryColor, 0.68)
    const companyContact = compactText(companyProfile.email, companyProfile.phone)
    const invoiceTerms = dueDate ? `Due by ${formatDate(dueDate)}` : 'Due on receipt'
    const lineRows: Array<{
      item: string
      description: string
      quantity: string
      rate: string
      amount: string
      amountValue: number
    }> = [
      ...(invoiceBasis?.particulars ?? []).map((item) => {
        const quantity = getParticularBillableQuantity(item)
        const amount = getParticularClientLineTotal(item)
        return {
          item: String(item.title || `Particular #${item.id}`),
          description: String(item.description || job?.title || '-'),
          quantity: quantity ? quantity.toFixed(2).replace(/\.00$/, '') : '-',
          rate: formatCurrency(toNumber(item.unit_price)),
          amount: formatCurrency(amount),
          amountValue: amount,
        }
      }),
      ...(invoiceBasis?.labour_entries ?? []).map((entry) => {
        const hours = toNumber(entry.hours)
        const amount = toNumber(entry.client_pay_amount)
        return {
          item: String(entry.worker_name || `Worker #${entry.worker}`),
          description: entry.work_date ? `Labour on ${formatDate(entry.work_date)}` : 'Labour charge',
          quantity: hours.toFixed(2).replace(/\.00$/, ''),
          rate: hours > 0 ? formatCurrency(amount / hours) : formatCurrency(amount),
          amount: formatCurrency(amount),
          amountValue: amount,
        }
      }),
      ...(invoiceBasis?.expenses ?? []).map((expense) => {
        const amount = toNumber(expense.amount)
        return {
          item: String(expense.title || `Expense #${expense.id}`),
          description: String(expense.description || expense.supplier_name || expense.worker_name || 'Billable expense'),
          quantity: '1',
          rate: formatCurrency(amount),
          amount: formatCurrency(amount),
          amountValue: amount,
        }
      }),
    ]
    const lineSubtotal = lineRows.reduce((total, row) => total + row.amountValue, 0)
    const subtotal = lineSubtotal > 0
      ? lineSubtotal
      : customerPayableTotal > 0
        ? customerPayableTotal
        : (Math.max(jobCostTotal - taxAmount, 0) || jobCostTotal)
    if (lineRows.length === 0) {
      lineRows.push({
        item: job?.title || documentType,
        description: job?.description || `${entity} service`,
        quantity: '1',
        rate: formatCurrency(subtotal),
        amount: formatCurrency(subtotal),
        amountValue: subtotal,
      })
    }

    const renderedLineRows = lineRows
      .map((row, index) => `
        <tr>
          <td class="center narrow">${index + 1}</td>
          <td><strong>${escapeHtml(row.item)}</strong></td>
          <td>${escapeHtml(row.description)}</td>
          <td class="right">${escapeHtml(row.quantity)}</td>
          <td class="right">${escapeHtml(row.rate)}</td>
          <td class="right">${escapeHtml(row.amount)}</td>
        </tr>
      `)
      .join('')
    const backendTotalRows = invoiceBasis?.total_rows ?? []
    const backendTaxRows = invoiceBasis?.tax_rows ?? []
    const backendIncludedTaxNote = String(invoiceBasis?.included_tax_note ?? '').trim()
    const includedTaxNames = taxBreakdown
      .filter((tax) => toBooleanFlag(tax.included))
      .map((tax) => String(tax.name || tax.tax_label_name || 'Tax'))
      .filter(Boolean)
    const hasAddedTaxRows = backendTaxRows.length > 0
      || taxBreakdown.some((tax) => !toBooleanFlag(tax.included) && toNumber(tax.tax_amount) > 0)
    const renderedTaxRows = (backendTaxRows.length > 0
      ? backendTaxRows
      : taxBreakdown
        .filter((tax) => !toBooleanFlag(tax.included))
        .map((tax) => ({
          label: String(tax.name || tax.tax_label_name || 'Tax'),
          amount: tax.tax_amount,
        })))
      .map((tax) => `
        <tr>
          <td>${escapeHtml(String(tax.label || 'Tax'))}</td>
          <td class="right">${escapeHtml(formatCurrency(toNumber(tax.amount)))}</td>
        </tr>
      `)
      .join('')
    const includedTaxLabel = backendIncludedTaxNote || (includedTaxNames.length > 0
      ? `Tax included in prices (${includedTaxNames.join(', ')})`
      : 'Tax included in prices')
    const renderedBackendTotalRows = backendTotalRows.length > 0
      ? backendTotalRows.map((row) => `
        <tr${row.kind === 'total' ? ' class="total"' : ''}>
          <td>${escapeHtml(String(row.label || ''))}</td>
          <td class="right">${escapeHtml(formatCurrency(toNumber(row.amount)))}</td>
        </tr>
      `).join('')
      : ''
    const renderedAdvancePaymentsRows = advancePaymentTransactions
      .map((payment) => `
        <tr>
          <td>${escapeHtml(payment.payment_date ? formatDate(payment.payment_date) : '-')}</td>
          <td>${escapeHtml(getPaymentWalletLabel(payment))}</td>
          <td>${escapeHtml(payment.reference || '-')}</td>
          <td class="right">${escapeHtml(formatCurrency(toNumber(payment.amount)))}</td>
        </tr>
      `)
      .join('')

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(documentType)} ${escapeHtml(job?.title || jobId || '')}</title>
          <style>
            @page { size: A4; margin: 0; }
            * { box-sizing: border-box; }
            html, body { min-height: 100%; }
            body { margin: 0; background: #f4f5f7; color: #1f2933; font-family: Arial, Helvetica, sans-serif; }
            .page { width: 210mm; min-height: 297mm; margin: 16px auto; background: #fff; padding: 23mm 14mm 18mm; display: flex; flex-direction: column; box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12); }
            h1, h2, h3, p { margin: 0; }
            .top { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; }
            .brand img { max-width: 150px; max-height: 92px; object-fit: contain; }
            .logo-fallback { width: 120px; height: 82px; border-radius: 18px; background: ${escapeHtml(companyProfile.primaryColor)}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 30px; font-weight: 700; }
            .company { text-align: right; color: #4b5563; font-size: 14px; line-height: 1.55; }
            .company strong { display: block; color: #111827; font-size: 15px; }
            .company .branch-name { color: #111827; font-weight: 700; }
            .title-row { display: grid; grid-template-columns: 1fr auto 1fr; gap: 20px; align-items: center; margin: 24px 0 28px; }
            .title-row::before, .title-row::after { content: ""; height: 1px; background: #d8dde3; }
            h1 { color: #111827; font-size: 16px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; }
            .billing { display: grid; grid-template-columns: 1.15fr 1fr; gap: 52px; margin-bottom: 28px; }
            .bill-to { color: #4b5563; font-size: 14px; line-height: 1.45; }
            .bill-to-title { color: #111827; font-weight: 500; margin-bottom: 4px; }
            .bill-to strong { display: block; color: #111827; font-size: 16px; margin-bottom: 6px; }
            .invoice-meta { width: 100%; border-collapse: collapse; font-size: 13px; }
            .invoice-meta td { padding: 5px 0; border: 0; }
            .invoice-meta td:first-child { color: #111827; font-weight: 700; }
            .invoice-meta td:last-child { color: #374151; text-align: left; padding-left: 30px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            .line-table th { background: ${tableHeaderColor}; color: #111827; font-weight: 700; padding: 10px 12px; text-align: left; border: 1px solid ${tableHeaderBorderColor}; }
            .line-table td { padding: 13px 12px; border: 1px solid #eceff3; vertical-align: top; line-height: 1.35; }
            .line-table .narrow { width: 40px; }
            .right { text-align: right; }
            .center { text-align: center; }
            .muted { color: #64748b; }
            .summary-area { display: grid; grid-template-columns: 1fr 310px; gap: 28px; margin-top: 18mm; align-items: start; }
            .thanks { color: #4b5563; font-size: 12px; padding-top: 4px; }
            .totals { border-collapse: collapse; font-size: 13px; }
            .totals td { border: 0; padding: 8px 10px; }
            .totals tr.rule td { border-top: 1px solid #d8dde3; }
            .totals tr.total td { border-top: 1px solid #d8dde3; border-bottom: 1px solid #d8dde3; font-weight: 700; padding-top: 12px; padding-bottom: 12px; }
            .balance { display: flex; justify-content: space-between; margin-top: 12px; border-top: 1px solid #d8dde3; border-bottom: 1px solid #d8dde3; padding: 13px 10px; font-size: 16px; font-weight: 700; }
            .advance { margin-top: 18px; font-size: 11px; }
            .advance th, .advance td { border: 1px solid #eceff3; padding: 7px 8px; }
            .advance th { background: #f8fafc; }
            .terms { margin-top: auto; padding-top: 36px; color: #4b5563; font-size: 12px; line-height: 1.45; }
            .terms strong { display: block; color: #111827; margin-bottom: 3px; }
            .footer { margin-top: 22px; border-top: 1px solid ${escapeHtml(companyProfile.primaryColor)}; padding-top: 8px; color: #64748b; font-size: 10px; text-align: center; }
            @media print {
              body { background: #fff; }
              .page { width: 210mm; min-height: 297mm; margin: 0; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <main class="page">
            <section class="top">
              <div class="brand">
                ${renderCompanyLogoHtml(companyProfile.logoUrl, companyProfile.name)}
              </div>
              <div class="company">
                <strong>${escapeHtml(companyProfile.name)}</strong>
                ${companyProfile.branchName ? `<div class="branch-name">${escapeHtml(companyProfile.branchName)}</div>` : ''}
                <div>${escapeHtml(companyProfile.address || 'Company address not set')}</div>
                ${companyContact ? `<div>${escapeHtml(companyContact)}</div>` : ''}
                ${companyProfile.registrationDetails ? `<div>${escapeHtml(companyProfile.registrationDetails)}</div>` : ''}
              </div>
            </section>

            <div class="title-row">
              <h1>${escapeHtml(documentType)}</h1>
            </div>

            <section class="billing">
              <div class="bill-to">
                <div class="bill-to-title">Bill To</div>
                <strong>${escapeHtml(invoiceBasis?.customer_name || invoiceBasis?.client || job?.client_name || 'Client not set')}</strong>
                <div>${escapeHtml(clientAddress || job?.address || 'Address not set')}</div>
                <div>${escapeHtml(compactText(invoiceBasis?.email || job?.email, invoiceBasis?.phone || job?.phone))}</div>
              </div>
              <div>
                <table class="invoice-meta">
                  <tbody>
                    <tr><td>${escapeHtml(documentType)}#</td><td>${escapeHtml(invoiceLabel)}</td></tr>
                    <tr><td>${escapeHtml(documentType)} Date</td><td>${escapeHtml(formatDate(invoiceDate))}</td></tr>
                    <tr><td>Terms</td><td>${escapeHtml(invoiceTerms)}</td></tr>
                    <tr><td>Due Date</td><td>${escapeHtml(dueDate ? formatDate(dueDate) : '-')}</td></tr>
                    <tr><td>${escapeHtml(`${entity} Ref`)}</td><td>${escapeHtml(String(job?.job_ref || jobId))}</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <table class="line-table">
              <thead>
                <tr>
                  <th class="center narrow">#</th>
                  <th>Item</th>
                  <th>Description</th>
                  <th class="right">Qty</th>
                  <th class="right">Rate</th>
                  <th class="right">Amount</th>
                </tr>
              </thead>
              <tbody>${renderedLineRows}</tbody>
            </table>

            <section class="summary-area">
              <div class="thanks">Thanks for your business.</div>
              <div>
                <table class="totals">
                  <tbody>
                    ${renderedBackendTotalRows || `
                      <tr><td>Sub Total</td><td class="right">${escapeHtml(formatCurrency(subtotal))}</td></tr>
                      ${taxAmount > 0 && !hasAddedTaxRows ? `<tr><td>Tax</td><td class="right">${escapeHtml(formatCurrency(taxAmount))}</td></tr>` : ''}
                      ${renderedTaxRows}
                      ${includedTaxAmount > 0 ? `<tr><td>${escapeHtml(includedTaxLabel)}</td><td class="right">${escapeHtml(formatCurrency(includedTaxAmount))}</td></tr>` : ''}
                      ${advancePayment > 0 ? `<tr><td>Advance paid</td><td class="right">-${escapeHtml(formatCurrency(advancePayment))}</td></tr>` : ''}
                      <tr class="total"><td>Total</td><td class="right">${escapeHtml(formatCurrency(jobCostTotal))}</td></tr>
                    `}
                  </tbody>
                </table>
                <div class="balance">
                  <span>Balance Due</span>
                  <span>${escapeHtml(formatCurrency(paymentDue))}</span>
                </div>
              </div>
            </section>

            ${renderedAdvancePaymentsRows ? `
              <table class="advance">
                <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th class="right">Amount</th></tr></thead>
                <tbody>${renderedAdvancePaymentsRows}</tbody>
              </table>
            ` : ''}

            <section class="terms">
              <strong>Terms &amp; Conditions</strong>
              All services provided are subject to the terms and conditions outlined in the contract agreement or engagement letter.
            </section>
            <section class="footer">
              ${escapeHtml(compactText(companyProfile.name, companyProfile.address, companyContact))}
            </section>
          </main>
        </body>
      </html>
    `
  }

  const printInvoiceBasis = () => {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1000,height=800')
    if (!printWindow) {
      toast.error('Unable to open print window. Use Preview, then print from the preview page.')
      return
    }

    printWindow.document.open()
    printWindow.document.write(buildInvoiceA4Html())
    printWindow.document.close()
    printWindow.focus()
    printWindow.setTimeout(() => printWindow.print(), 250)
  }

  const previewBackPath = withServiceInstanceId(`/finance/invoices/${jobId}`, serviceInstanceId)
  const handlePrintInlinePreview = () => {
    const frameWindow = previewFrameRef.current?.contentWindow
    if (!frameWindow) {
      toast.error('Preview is still loading. Please try again.')
      return
    }
    frameWindow.focus()
    frameWindow.print()
  }

  const downloadInvoicePdf = async () => {
    try {
      const [{ jsPDF }, autoTableModule] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
      ])

      const autoTable =
        (autoTableModule as {
          default?: (doc: unknown, options: unknown) => void
          autoTable?: (doc: unknown, options: unknown) => void
        }).default ??
        (autoTableModule as {
          default?: (doc: unknown, options: unknown) => void
          autoTable?: (doc: unknown, options: unknown) => void
        }).autoTable

      if (!autoTable) {
        throw new Error('autotable plugin not available')
      }

      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const brandPrimary = hexToRgb(companyProfile.primaryColor)
      const brandAccent: [number, number, number] = [10, 132, 255]
      const sectionHeader: [number, number, number] = [30, 64, 175]
      const rows = summaryRows

      doc.setFillColor(...brandPrimary)
      doc.roundedRect(14, 6, 14, 14, 2, 2, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(companyProfile.name.slice(0, 2).toUpperCase(), 17, 15)

      doc.setTextColor(17, 24, 39)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(15)
      doc.text(companyProfile.name, 34, 10)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      if (companyProfile.branchName) {
        doc.setFont('helvetica', 'bold')
        doc.text(companyProfile.branchName, 34, 16)
        doc.setFont('helvetica', 'normal')
      }
      doc.text(companyProfile.address || 'Company address not set', 34, companyProfile.branchName ? 22 : 16)
      doc.text(compactText(companyProfile.email, companyProfile.phone), 34, companyProfile.branchName ? 28 : 22)
      if (companyProfile.registrationDetails) {
        doc.text(companyProfile.registrationDetails, 34, companyProfile.branchName ? 34 : 28)
      }

      doc.setFillColor(...brandPrimary)
      doc.rect(14, 30, pageWidth - 28, 2.2, 'F')

      doc.setFillColor(...brandAccent)
      doc.roundedRect(pageWidth - 72, 7, 58, 15, 2, 2, 'F')
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.text(`${documentType}: ${invoiceLabel}`, pageWidth - 68, 16)

      doc.setTextColor(17, 24, 39)
      const title = `${documentType} for ${job?.title || jobId || entity}`
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(16)
      doc.text(title, 14, 42)

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(11)
      let cursorY = 50

      const writeMeta = (label: string, value: string) => {
        const lines = doc.splitTextToSize(`${label}: ${value}`, 180)
        doc.text(lines, 14, cursorY)
        cursorY += lines.length * 6
      }

      writeMeta('Invoice Date', formatDate(invoiceDate))
      writeMeta('Due Date', dueDate ? formatDate(dueDate) : '-')
      writeMeta('Client', invoiceBasis?.customer_name || invoiceBasis?.client || job?.client_name || 'Client not set')
      writeMeta(`${entity} Ref`, String(job?.job_ref || jobId))
      writeMeta('Address', clientAddress || job?.address || 'Address not set')
      writeMeta('Email', invoiceBasis?.email || job?.email || 'Email not set')
      writeMeta('Phone', invoiceBasis?.phone || job?.phone || 'Phone not set')

      autoTable(doc, {
        startY: cursorY + 2,
        head: [['Line', 'Amount']],
        body: rows,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 2.5,
          lineColor: [209, 213, 219],
        },
        headStyles: {
          fillColor: brandPrimary,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        columnStyles: {
          1: { halign: 'right' },
        },
        margin: {
          left: 14,
          right: 14,
        },
      })

      const withLastAutoTable = doc as typeof doc & { lastAutoTable?: { finalY?: number } }
      cursorY = (withLastAutoTable.lastAutoTable?.finalY ?? cursorY + 20) + 8

      const ensurePageSpace = () => {
        if (cursorY > 250) {
          doc.addPage()
          cursorY = 18
        }
      }

      const renderSectionTable = (titleText: string, head: string[], body: string[][]) => {
        if (!body.length) {
          return
        }

        ensurePageSpace()
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(12)
        doc.text(titleText, 14, cursorY)

        autoTable(doc, {
          startY: cursorY + 3,
          head: [head],
          body,
          theme: 'grid',
          styles: {
            fontSize: 9,
            cellPadding: 2,
            lineColor: [209, 213, 219],
          },
          headStyles: {
            fillColor: sectionHeader,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          margin: {
            left: 14,
            right: 14,
          },
        })

        const withAutoTable = doc as typeof doc & { lastAutoTable?: { finalY?: number } }
        cursorY = (withAutoTable.lastAutoTable?.finalY ?? cursorY + 25) + 8
      }

      const particularsBody = (invoiceBasis?.particulars ?? []).map((item) => [
        String(item.title || item.description || `Particular #${item.id}`),
        String(getParticularBillableQuantity(item) || '-'),
        formatCurrency(toNumber(item.unit_price)),
        formatCurrency(getParticularClientLineTotal(item)),
      ])

      renderSectionTable('Inventory', ['Title', 'Qty', 'Unit Price', 'Line Total'], particularsBody)

      const labourBody = (invoiceBasis?.labour_entries ?? []).map((entry) => [
        String(entry.worker_name || `Worker #${entry.worker}`),
        entry.work_date ? formatDate(entry.work_date) : '-',
        toNumber(entry.hours).toFixed(2),
        formatCurrency(toNumber(entry.client_pay_amount)),
      ])

      renderSectionTable('Labour Charges', ['Worker', 'Date', 'Hours', 'Client Charge'], labourBody)

      const expensesBody = (invoiceBasis?.expenses ?? []).map((expense) => [
        String(expense.title || expense.description || `Expense #${expense.id}`),
        String(expense.supplier_name || expense.worker_name || '-'),
        formatCurrency(toNumber(expense.amount)),
      ])

      renderSectionTable('Expenses', ['Title', 'Supplier / Worker', 'Amount'], expensesBody)

      const taxBody = taxBreakdown.map((tax) => [
        String(tax.name || tax.tax_label_name || 'Tax'),
        String(tax.value ?? '-'),
        tax.included ? 'Included' : 'Added',
        formatCurrency(toNumber(tax.tax_amount)),
      ])

      renderSectionTable('Tax', ['Tax', 'Rate / Value', 'Mode', 'Amount'], taxBody)

      const advancePaymentsBody = advancePaymentTransactions.map((payment) => [
        payment.payment_date ? formatDate(payment.payment_date) : '-',
        getPaymentWalletLabel(payment),
        payment.reference || '-',
        formatCurrency(toNumber(payment.amount)),
      ])

      renderSectionTable('Advance Payment Transactions', ['Date', 'Method', 'Reference', 'Amount'], advancePaymentsBody)

      const pageCount = doc.getNumberOfPages()
      for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page)
        doc.setDrawColor(...brandPrimary)
        doc.setLineWidth(0.35)
        doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12)

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(71, 85, 105)
        doc.text(`${companyProfile.name} · ${companyProfile.address || 'Address not set'}`, 14, pageHeight - 7)
        doc.text(`Page ${page} of ${pageCount}`, pageWidth - 34, pageHeight - 7)
      }

      const safeJobRef = String(job?.job_ref || job?.id || jobId || 'job').replace(/[^a-zA-Z0-9-_]/g, '-')
      doc.save(`${documentTypeLower}-${safeJobRef}.pdf`)
    } catch {
      toast.error('Unable to generate PDF right now. Please try again.')
    }
  }

  if (variant === 'a4-preview') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon">
              <Link to={previewBackPath}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">{documentType} A4 Preview</h1>
              <p className="text-sm text-muted-foreground">{`${entity}: ${job?.title || jobId}`}</p>
            </div>
          </div>
          <Button onClick={handlePrintInlinePreview} disabled={isLoading}>
            <Printer className="mr-2 h-4 w-4" />
            Print {documentType}
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-[calc(100vh-180px)] min-h-[720px] w-full rounded-xl" />
        ) : (
          <iframe
            ref={previewFrameRef}
            title={`${documentType} A4 preview`}
            srcDoc={buildInvoiceA4Html()}
            className="h-[calc(100vh-180px)] min-h-[720px] w-full rounded-xl border bg-muted"
          />
        )}
      </div>
    )
  }

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-2">
      <Button asChild variant="outline" size={variant === 'embedded' ? 'sm' : 'default'}>
        <Link to={withServiceInstanceId(`/finance/invoices/${jobId}/a4-preview`, serviceInstanceId)}>
          <Eye className="mr-2 h-4 w-4" />
          Preview {documentType}
        </Link>
      </Button>
      <Button onClick={() => void downloadInvoicePdf()} variant="outline" size={variant === 'embedded' ? 'sm' : 'default'}>
        <Download className="mr-2 h-4 w-4" />
        Download {documentType}
      </Button>
      <Button onClick={printInvoiceBasis} variant="outline" size={variant === 'embedded' ? 'sm' : 'default'}>
        <Printer className="mr-2 h-4 w-4" />
        Print {documentType}
      </Button>
      <Button
        onClick={handleCreateDraftInvoice}
        disabled={!jobId || createInvoiceMutation.isPending || hasActiveInvoice}
        size={variant === 'embedded' ? 'sm' : 'default'}
      >
        <FilePlus2 className="mr-2 h-4 w-4" />
        {hasActiveInvoice ? 'Active Invoice Exists' : 'Create Draft Invoice'}
      </Button>
      {variant === 'embedded' ? (
        <Button asChild variant="ghost" size="sm">
          <Link to={withReturnTo(withServiceInstanceId(`/finance/invoices/${jobId}`, serviceInstanceId), currentReturnPath)}>
            Open full page
          </Link>
        </Button>
      ) : null}
    </div>
  )

  return (
    <div className="space-y-6">
      {variant === 'page' ? (
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to={backPath}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-foreground">Invoices</h1>
            <p className="text-muted-foreground">{`${entity}: ${job?.title || jobId}`}</p>
          </div>
          {actionButtons}
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Invoices</h2>
            <p className="text-sm text-muted-foreground">
              {`Preview billing basis and manage draft, issued, and voided invoices for this ${entityLower}.`}
            </p>
          </div>
          {actionButtons}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ReceiptText className="h-4 w-4" />
              {isSnapshotFrozen ? 'Stored Invoice Snapshot' : `Live ${documentType} Basis`}
            </CardTitle>
            <CardDescription>
              {isSnapshotFrozen
                ? 'Showing the JSON snapshot saved on this invoice. Line items and totals stay fixed after issue.'
                : `Shows customer payable, customer-payable inventory, customer-payable worker payout, and billable expenses, then subtracts the ${entityLower} advance payment from the amount due.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Customer Payable</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(customerPayableTotal)}</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Inventory Payable By Customer</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(particularsTotal)}</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Expenses</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(billableExpensesTotal)}</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Worker Payout Payable By Customer</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(labourChargesTotal)}</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Invoice Total</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(jobCostTotal)}</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Advance Payment</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(advancePayment)}</p>
              </div>
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Payment Due</p>
                <p className="mt-2 text-lg font-semibold">{formatCurrency(paymentDue)}</p>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <h3 className="font-semibold">Bill To</h3>
                <p className="mt-2 text-sm font-medium text-foreground">{invoiceBasis?.customer_name || invoiceBasis?.client || job?.client_name || 'Client not set'}</p>
                <p className="mt-1 text-sm text-muted-foreground">{clientAddress || job?.address || 'Address not set'}</p>
                <p className="mt-1 text-sm text-muted-foreground">{invoiceBasis?.email || job?.email || 'Email not set'}</p>
                <p className="mt-1 text-sm text-muted-foreground">{invoiceBasis?.phone || job?.phone || 'Phone not set'}</p>
              </div>
              <div>
                <h3 className="font-semibold">{`${entity} Info`}</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>{`${entity}: ${job?.title || invoiceBasis?.title || jobId}`}</p>
                  <p>Scheduled: {invoiceBasis?.scheduled_start ? formatDate(invoiceBasis.scheduled_start) : 'Not scheduled'}</p>
                  <p>Logged hours: {toNumber(invoiceBasis?.total_hours).toFixed(2)} hrs</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-3 font-semibold">Inventory</h3>
              {invoiceBasis?.particulars?.length ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceBasis.particulars.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.title || item.description || `Particular #${item.id}`}</TableCell>
                          <TableCell className="text-right">{getParticularBillableQuantity(item)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(toNumber(item.unit_price))}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(getParticularClientLineTotal(item))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                  {`No particulars are currently attached to this ${entityLower}.`}
                </div>
              )}
            </div>

            {invoiceBasis?.labour_entries?.length ? (
              <div>
                <h3 className="mb-3 font-semibold">Labour Charges</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Worker</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Client Charge</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceBasis.labour_entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{entry.worker_name || `Worker #${entry.worker}`}</TableCell>
                          <TableCell>{entry.work_date ? formatDate(entry.work_date) : '-'}</TableCell>
                          <TableCell className="text-right">{toNumber(entry.hours).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(toNumber(entry.client_pay_amount))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                {`No labour charges are currently attached to this ${entityLower}.`}
              </div>
            )}

            {invoiceBasis?.expenses?.length ? (
              <div>
                <h3 className="mb-3 font-semibold">Expenses</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Supplier / Worker</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceBasis.expenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>{expense.title || expense.description || `Expense #${expense.id}`}</TableCell>
                          <TableCell>{expense.supplier_name || expense.worker_name || '-'}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(toNumber(expense.amount))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                {`No expenses are currently attached to this ${entityLower}.`}
              </div>
            )}

            {advancePaymentTransactions.length ? (
              <div>
                <h3 className="mb-3 font-semibold">Advance Payment Transactions</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {advancePaymentTransactions.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.payment_date ? formatDate(payment.payment_date) : '-'}</TableCell>
                          <TableCell>{getPaymentWalletLabel(payment)}</TableCell>
                          <TableCell>{payment.reference || '-'}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(toNumber(payment.amount))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                {`No advance payment transactions are currently recorded for this ${entityLower}.`}
              </div>
            )}

            <div>
              <h3 className="mb-3 font-semibold">Invoice Total</h3>
              <div className="rounded-md border">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>Customer payable</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(customerPayableTotal)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Inventory payable by customer</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(particularsTotal)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Expenses</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(billableExpensesTotal)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Worker payout payable by customer</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(labourChargesTotal)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-semibold">Invoice total</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(jobCostTotal)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Created Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {hasActiveInvoice ? (
              <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                {`This ${entityLower} already has an invoice, so creating another one is disabled in the UI.`}
              </div>
            ) : null}
            {hasOnlyVoidInvoices ? (
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                {`All previous invoices for this ${entityLower} are void. You can create a new draft invoice.`}
              </div>
            ) : null}

            {activeInvoices.length ? (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Button
                  type="button"
                  size="sm"
                  variant={invoiceStatusFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setInvoiceStatusFilter('all')}
                >
                  All ({activeInvoices.length})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={invoiceStatusFilter === 'issued' ? 'default' : 'outline'}
                  onClick={() => setInvoiceStatusFilter('issued')}
                >
                  Issued ({activeInvoices.filter((entry) => entry.status === 'issued').length})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={invoiceStatusFilter === 'draft' ? 'default' : 'outline'}
                  onClick={() => setInvoiceStatusFilter('draft')}
                >
                  Draft ({activeInvoices.filter((entry) => entry.status === 'draft').length})
                </Button>
              </div>
            ) : null}

            {filteredActiveInvoices.length ? (
              <div className="space-y-3">
                {filteredActiveInvoices.map(renderInvoiceCard)}
              </div>
            ) : allInvoices.length ? (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                No {invoiceStatusFilter === 'all' ? 'active' : invoiceStatusFilter} {`invoices for this ${entityLower} right now.`} Voided invoices remain available in history below.
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                {`No invoices yet for this ${entityLower}.`}
              </div>
            )}

            {voidInvoices.length ? (
              <>
                <Separator className="my-6" />
                <Collapsible>
                  <div className="rounded-lg border border-dashed border-border/70 bg-muted/10">
                    <CollapsibleTrigger asChild>
                      <button className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                        <div>
                          <p className="font-medium text-foreground">Voided invoices</p>
                          <p className="text-sm text-muted-foreground">{voidInvoices.length} historical invoice{voidInvoices.length === 1 ? '' : 's'}</p>
                        </div>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-3 border-t border-border/70 px-4 py-4">
                      {voidInvoices.map(renderInvoiceCard)}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              </>
            ) : null}

            <Separator className="my-6" />
            <p className="text-sm text-muted-foreground">
              {`Totals include billable expenses and deduct the ${entityLower} advance payment. Until completion, downloads are quotations.`}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
